/* WakeUpInsightCard — Pet pattern learning.
 * Shows when user has >=5 SOS sessions and >=3 cluster at same hour.
 * Offers to schedule a preventive daily break before the peak time. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Brain, Calendar, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WakeUpInsightCard({ onPointsChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // peak_hour being acted on

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/wakeup-sos/insights`, { withCredentials: true });
      setSuggestions(data?.suggestions || []);
      setTotalSessions(data?.total_sessions || 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const accept = async (sug) => {
    setBusy(sug.peak_hour);
    try {
      const { data } = await axios.post(`${API}/wakeup-sos/insights/accept`, {
        peak_hour: sug.peak_hour,
        suggested_break_at: sug.suggested_break_at,
      }, { withCredentials: true });
      toast.success(data?.message || 'Daglig paus inbokad 💖');
      setSuggestions((prev) => prev.filter((s) => s.peak_hour !== sug.peak_hour));
      onPointsChange?.();
    } catch { toast.error('Kunde inte boka in pausen'); }
    finally { setBusy(null); }
  };

  const dismiss = async (sug) => {
    setBusy(sug.peak_hour);
    try {
      await axios.post(`${API}/wakeup-sos/insights/dismiss`, {
        peak_hour: sug.peak_hour,
      }, { withCredentials: true });
      setSuggestions((prev) => prev.filter((s) => s.peak_hour !== sug.peak_hour));
    } catch { /* silent */ }
    finally { setBusy(null); }
  };

  if (loading || suggestions.length === 0) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="wakeup-insight-card">
      {suggestions.map((sug) => (
        <div key={sug.peak_hour}
          className="neo-card p-3 bg-gradient-to-br from-[#FFE8A3] via-[#FFC8DD] to-[#E8E0FF] relative"
          data-testid={`insight-${sug.peak_hour}`}>
          <button onClick={() => dismiss(sug)} disabled={busy === sug.peak_hour}
            className="absolute top-2 right-2 opacity-50 hover:opacity-100 p-1"
            data-testid={`insight-dismiss-${sug.peak_hour}`}>
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-2 pr-6">
            <div className="text-3xl shrink-0">🐾</div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase opacity-60 tracking-wide flex items-center gap-1">
                <Brain className="w-3 h-3" /> Pet-insikt — mönster upptäckt
              </p>
              <p className="font-bold text-sm leading-tight mt-1">{sug.message}</p>
              <p className="text-[10px] opacity-60 italic mt-1">
                Baserat på dina senaste {totalSessions} sessioner under 30 dagar
              </p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => accept(sug)} disabled={busy === sug.peak_hour}
              className="neo-btn flex-1 py-2 bg-[#B9FBC0] text-xs font-bold flex items-center justify-center gap-1"
              data-testid={`insight-accept-${sug.peak_hour}`}>
              {busy === sug.peak_hour
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <><Calendar className="w-3.5 h-3.5" /> Ja, boka in daglig paus kl {sug.suggested_break_at}</>}
            </button>
            <button onClick={() => dismiss(sug)} disabled={busy === sug.peak_hour}
              className="neo-btn px-3 py-2 bg-white text-xs font-bold"
              data-testid={`insight-no-${sug.peak_hour}`}>
              Inte nu
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
